import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button } from "@mui/material";
import { PlusIcon } from "assets";
import { QUERY_KEYS } from "consts";
import { SiteDTO, uploadSites, exportSitesData } from "api";
import { useGetAllowedValues, useNotify, usePagination, useSitesPaginated, useTableFilters } from "hooks";
import { USER_ROLES, useNewBreadcrumb, useUserRole } from "recoils";
import { getAllowedValuesHelper } from "utils";
import { Loading, Pagination, ActiveFilters, TableFilterToolbar, CustomMaterialTable } from "components";
import { RecUploadButton } from "components/buttons/rec-upload-button";
import { ExportButton } from "components/buttons/exportButton";

import { SITES_PAGE } from "./consts";
import { SiteColumns } from "./components/site-editor-form/components/SiteColumns";
import { AddSiteModal } from "./components/site-editor-form/components/AddSiteModal";
import { createSiteFilterConfig, SiteFilters } from "./filter-config";

export const TabSite = ({ state }: { state: string }) => {
  const notify = useNotify();
  const userRole = useUserRole();
  const navigate = useNavigate();
  const isActiveTab = state === "active";
  const localStoragePaginationKey = `site-management-${isActiveTab ? "activeTab" : "inactiveTab"}-pagination`;

  useNewBreadcrumb(SITES_PAGE.TITLE);

  // ---- Pagination ----
  const { pagination, paginatedQuery, onPageChange, onSizeChange, updateFromApiResponse } = usePagination({
    localStorageKey: localStoragePaginationKey,
    initialPage: 0,
    initialSize: 50,
  });

  // ---- Filters (using new standardized hook) ----
  const filterConfig = useMemo(() => createSiteFilterConfig(isActiveTab), [isActiveTab]);

  const tableFilters = useTableFilters<SiteFilters>({
    config: filterConfig,
    pagination: { onPageChange, resetPage: () => onPageChange(1) },
  });

  // ---- Data Fetching ----
  const { data: sitesData, isLoading, isFetching } = useSitesPaginated(
    { ...paginatedQuery, isActive: isActiveTab },
    tableFilters.getQueryParams()
  );
  const { data: allowedValues } = useGetAllowedValues();

  // ---- Local State ----
  const [addSiteModalOpen, setAddSiteModalOpen] = useState(false);

  const sites = sitesData?.sites ?? [];

  // ---- Handlers ----
  const handleCloseSiteModal = () => {
    if (addSiteModalOpen) {
      setAddSiteModalOpen(false);
    }
  };

  const handleNavigateToSiteDetails = (siteData: SiteDTO) => {
    if (siteData) {
      navigate(`/sites/site-details/${siteData.id}`);
    } else {
      notify.error("Site id not found.");
    }
  };

  // ---- Effects ----
  useEffect(() => {
    if (sitesData?.pageResult) {
      updateFromApiResponse(sitesData.pageResult);
    }
  }, [sitesData?.pageResult, updateFromApiResponse]);

  // ---- Get props from table filters hook ----
  const mrtFilterProps = tableFilters.getMRTFilterProps();
  const toolbarProps = tableFilters.getToolbarProps();
  const activeFiltersProps = tableFilters.getActiveFiltersProps();

  return (
    <Box>
      {/* Toolbar with filter buttons and action buttons */}
      <TableFilterToolbar {...toolbarProps}>
        <RecUploadButton
          label="Upload Data"
          uploadApiCall={uploadSites}
          queryKeyToInvalidate={QUERY_KEYS.SITES}
          availableToRoles={[USER_ROLES.MODULE_ADMIN, USER_ROLES.DATA_PROVIDER, USER_ROLES.DATA_VALIDATOR, USER_ROLES.DATA_REVIEWER]}
          extraReqParams={[["configuration", "true"]]}
          marginTop="0px"
        />
        <Button
          sx={{ height: "36px", width: "140px", borderRadius: 2 }}
          variant="contained"
          onClick={() => setAddSiteModalOpen(true)}
          startIcon={<PlusIcon />}
          disabled={userRole.isAuditor || userRole.isDataProvider}
        >
          Add Site
        </Button>
        <ExportButton
          sx={{ height: "36px", width: "200px", borderRadius: 2 }}
          exportFunction={exportSitesData}
          exportParams={{}}
          exportTitle="site_management_export"
          exportText="Export to Excel"
          exportingText="Loading..."
          successMessage="Export completed successfully!"
          errorMessage="Failed to Export to Excel"
          isZipFile={false}
          fileExtension=".xlsx"
          openFromActiveTab={isActiveTab}
        />
      </TableFilterToolbar>

      {/* Active filters display */}
      <ActiveFilters
        appliedFilters={activeFiltersProps.appliedFilters}
        onClearFilter={(filterId) => {
          activeFiltersProps.onClearFilter(filterId);
        }}
        displayNameMap={activeFiltersProps.displayNameMap}
      />

      {/* Table */}
      {isFetching || isLoading ? (
        <Loading />
      ) : (
        <>
          <CustomMaterialTable
            state={{ isLoading }}
            enableSorting
            enablePagination
            manualPagination
            rowCount={pagination.size}
            getRowId={(row: any) => row.id}
            data={sites}
            columns={SiteColumns}
            enableGlobalFilter={false}
            enableTopToolbar={false}
            enableRowVirtualization={false}
            enableColumnFilters={mrtFilterProps.enableColumnFilters}
            manualFiltering={mrtFilterProps.manualFiltering}
            onColumnFiltersChange={mrtFilterProps.onColumnFiltersChange}
            muiTableHeadCellFilterTextFieldProps={mrtFilterProps.muiTableHeadCellFilterTextFieldProps}
            initialState={mrtFilterProps.initialState}
            muiTableContainerProps={{
              sx: {
                height: "calc(100vh - 330px)",
                minHeight: "200px",
                width: "calc(100vw - 150px)",
              },
            }}
            rowClickable
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => handleNavigateToSiteDetails(row.original),
            })}
            positionActionsColumn="last"
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "10px",
            }}
          >
            <Pagination
              page={pagination.page + 1}
              rowsCount={pagination.totalRows}
              rowsPerPage={pagination.size}
              rowsPerPageOptions={[50, 100, 200]}
              onPageChange={onPageChange}
              onRowsPerPageChange={onSizeChange}
            />
          </Box>
        </>
      )}
      <AddSiteModal
        isOpen={addSiteModalOpen}
        onClose={handleCloseSiteModal}
        allowedValues={getAllowedValuesHelper(allowedValues)}
        openFromActiveTab={isActiveTab}
      />
    </Box>
  );
};
